import type { PDPProductThumbnail } from "@components/commerce/product/right-panel/buy-card/types";
import type { Product, ProductVariant } from "@lib/product/types";
import { Attributes } from "@lib/enums/ProductAttributes";
import { getBackgroundColorValue, getProductAttributeValue } from "@lib/utils/attributesUtils";
import type { AlgoliaProduct } from "@lib/wrappers/algolia/types";

export const isAnyVariantsInStock = (variants: ProductVariant[]): boolean => {
    const results = [];

    variants.forEach((variant) => {
        variant.availability.forEach((channel) => {
            results.push(channel.isOnStock);
        });
    });

    return results.some((r) => r === true);
};

export const getProductImageThumbnails = (
    product: Product,
    locale: string
): PDPProductThumbnail => {
    const { attributes, media, variants, name } = product;
    const colorCode = getProductAttributeValue(attributes, Attributes.colorCode);
    const articleNumber = getProductAttributeValue(attributes, Attributes.articleNumber);
    const dominantColor = getProductAttributeValue(attributes, Attributes.dominantColor, locale);
    const pageTitle = getProductAttributeValue(attributes, Attributes.pageTitle, locale);
    const backgroundColor = getBackgroundColorValue(attributes);
    const [firstImage] = media;

    const available = isAnyVariantsInStock(variants);

    return {
        productName: name,
        articleNumber,
        dominantColor,
        colorCode,
        image: {
            url: firstImage.url,
            label: firstImage.label ?? "",
        },
        available,
        backgroundColor,
        pageTitle,
    };
};

export const getRelatedProductImageThumbnails = (product: AlgoliaProduct): PDPProductThumbnail[] =>
    product?.productColors?.map((relatedProduct) => ({
        image: {
            url: relatedProduct.image ?? "",
            label: relatedProduct.imageLabel ?? "",
        },
        productName: relatedProduct.name ?? product.name,
        articleNumber: product.articleNumber,
        dominantColor: relatedProduct.dominantColor ?? product.dominantColor,
        colorCode: relatedProduct.colorCode,
        available: relatedProduct.hasStock,
        ...(product.pageTitle && { pageTitle: product.pageTitle }),
    })) ?? [];

export const calculatePrice = (centAmount: number, fractionDigits: number) => {
    const divisor = Math.pow(10, fractionDigits);
    return (centAmount / divisor).toFixed(fractionDigits);
};

export const calculatePriceValidUntil = (
    discountPercentage: number | Record<string, never>
): Date => {
    const priceValidUntil: Date = new Date();
    if (typeof discountPercentage === "number") {
        priceValidUntil.setHours(23, 59, 59, 999);
    } else {
        priceValidUntil.setFullYear(priceValidUntil.getFullYear() + 1);
    }
    return priceValidUntil;
};
